/**
 * LOS CULTIVOS CURADOS: UNA SOLA LISTA (01-10-2026, plan cultivos SIEX, F1).
 *
 * Hasta hoy había tres listas de cultivos que no se conocían entre sí:
 *   - `CropCatalogSelect` (9 claves `cropCycles.defaultCrops.*` traducidas en
 *     los 9 idiomas de la app),
 *   - la TABLA del cuaderno (`src/lib/cuaderno/cultivos.ts`: 27 especies con
 *     sus sinónimos en 9 idiomas, espejada en Deno),
 *   - `CROP_ALIASES` del asistente (`tools/registryMapa.ts`: 24 nombres → el
 *     nombre del cultivo en el Registro de Fitosanitarios, regfiweb).
 * Esta lista las une, y a cada cultivo le pone su IDENTIDAD: el código SIEX
 * (catálogo PRODUCTOS del FEGA), que es lo que pedirá el CUE digital desde el
 * 01-01-2027. El test `cropCurated.fixture.test.ts` comprueba cada código y
 * cada nombre oficial contra el CSV de SIEX del 28-09-2026.
 *
 * Qué es cada campo, y por qué los sinónimos van en TRES cajones distintos:
 *   - `nombres`: traducciones y plurales que SON ese cultivo y nada más
 *     (Pomodoro, Tomato, tomates). El nivel B de la migración segura: se
 *     emparejan solos (decisión D1 de Pablo, 30-09).
 *   - `erratas` («berengena»): nunca solas. Se proponen; las aprueba una persona
 *     fila a fila (D2).
 *   - `tipos` («cherry», «pepino holandés», «squash»): dicen algo más que la
 *     especie, o pueden ser otra. Solo sugerencia (nivel C).
 *   La TABLA del cuaderno las acepta TODAS (nombres + erratas + tipos), igual
 *   que antes: el cuaderno no cambia ni un byte (ver cultivos.ts).
 *
 * Decisiones de código que no son evidentes (todas mirando el CSV de SIEX):
 *   - Judía → 49 ALUBIA. SIEX dio de baja 180 JUDÍA el 31/12/2022 y desde
 *     entonces Phaseolus vulgaris, judía verde incluida, solo vive en 49 ALUBIA
 *     (la única entrada ACTIVA de esa especie). La etiqueta sigue «Judía».
 *   - Col → 172 REPOLLO. 170 COL está de baja desde el 31/12/2022; «repollo» ya
 *     era sinónimo de «Col» en el cuaderno. Las otras coles (lombarda, china,
 *     de Bruselas…) tienen su código y no se curan aquí.
 *   - Pepinillo: 188 PEPINILLOS está de baja desde el 31/12/2022 (va a 165
 *     PEPINO). No es un cultivo curado: queda en `regfiwebTipos` de Pepino para
 *     que el asistente siga preguntando al registro por «Pepinillo».
 *   - Fresa (219) y Fresón (938) son DOS cultivos activos en SIEX (grupo fresa /
 *     grupo fresón de Fragaria × ananassa). «fresón» es el nombre oficial de 938;
 *     en el cuaderno sigue imprimiéndose «Fresa», como hasta hoy.
 *   - Haba → 41 HABA y Guisante → 40 GUISANTE: «HABA FRESCA» (161) y «GUISANTE
 *     EN VERDE» (44) están de baja desde 2014.
 *
 * `etiquetas`: las de `cropCycles.defaultCrops` de los 9 ficheros de idioma para
 * los 9 cultivos por defecto; para el resto, la palabra que ya estaba en los
 * sinónimos del cuaderno. Un idioma sin palabra conocida se deja fuera: no se
 * inventa.
 *
 * PURO: sin red ni imports (viaja a Deno en _shared/cultivosCurados.generated.ts
 * y al motor del cuaderno).
 */

export type IdiomaCultivo = "es" | "en" | "it" | "fr" | "pt" | "ar" | "zh" | "bm" | "man";

export interface CultivoCurado {
  /** Código SIEX (catálogo PRODUCTOS del FEGA). La identidad del cultivo. */
  siex: number;
  /** Nombre SIEX tal cual, p. ej. 'TOMATE'. */
  oficial: string;
  /** Nombre castellano que se enseña y se guarda como snapshot, p. ej. 'Tomate'. Es la clave de la TABLA del cuaderno. */
  visible: string;
  /** Clave i18n `cropCycles.defaultCrops.<i18nKey>` de los 9 cultivos por defecto de la app. */
  i18nKey?: string;
  /** Orden de invernadero (1 = el más habitual): `crop_catalog.greenhouse_rank`. */
  rango: number;
  /** Sinónimos SEGUROS (traducciones y plurales), minúsculas y sin tildes. Nivel B: emparejan solos. */
  nombres: string[];
  /** Erratas: nunca automáticas, solo sugerencia. */
  erratas?: string[];
  /** Tipos («cherry») o nombres que pueden ser otra cosa: solo sugerencia. */
  tipos?: string[];
  /** Nombre del cultivo en el Registro de Fitosanitarios del MAPA (regfiweb). */
  regfiweb?: string;
  /** Tipo → nombre en regfiweb cuando el registro lo separa y SIEX no (pepinillo). No empareja cultivo. */
  regfiwebTipos?: Readonly<Record<string, string>>;
  /** Cómo se llama en cada idioma de la app. Falta un idioma = no se sabe. */
  etiquetas?: Partial<Record<IdiomaCultivo, string>>;
}

export const CULTIVOS_CURADOS: readonly CultivoCurado[] = [
  // ── Los 27 del cuaderno, en el orden de su TABLA (CU2, 27-09-2026) ──────────
  {
    siex: 197, oficial: "TOMATE", visible: "Tomate", i18nKey: "tomato", rango: 1,
    nombres: ["tomati", "番茄", "طماطم", "tomates", "pomodoro", "pomodori", "tomato", "tomatoes", "tomatier", "tomateiro"],
    tipos: ["tomate cherry", "cherry"],
    regfiweb: "Tomate",
    etiquetas: { es: "Tomate", en: "Tomato", it: "Pomodoro", fr: "Tomate", pt: "Tomate", ar: "طماطم", zh: "番茄", bm: "Tomati", man: "Tomati" },
  },
  {
    siex: 152, oficial: "PIMIENTO", visible: "Pimiento", i18nKey: "pepper", rango: 2,
    nombres: ["piman", "foronto", "甜椒", "فلفل", "pimientos", "peperone", "peperoni", "pepper", "peppers", "sweet pepper", "bell pepper", "poivron", "poivrons", "pimento", "pimentao", "pimento doce"],
    regfiweb: "Pimiento",
    etiquetas: { es: "Pimiento", en: "Pepper", it: "Peperone", fr: "Poivron", pt: "Pimento", ar: "فلفل", zh: "甜椒", bm: "Piman", man: "Fóronto" },
  },
  {
    siex: 165, oficial: "PEPINO", visible: "Pepino", i18nKey: "cucumber", rango: 3,
    nombres: ["konkonbere", "konkɔnbɛrɛ", "konkonburu", "黄瓜", "خيار", "pepinos", "cetriolo", "cetrioli", "cucumber", "cucumbers", "concombre", "concombres"],
    tipos: ["pepino holandes", "pepino largo", "pepino corto"],
    regfiweb: "Pepino",
    regfiwebTipos: { pepinillo: "Pepinillo", pepinillos: "Pepinillo" },
    etiquetas: { es: "Pepino", en: "Cucumber", it: "Cetriolo", fr: "Concombre", pt: "Pepino", ar: "خيار", zh: "黄瓜", bm: "Konkɔnbɛrɛ", man: "Konkonburu" },
  },
  {
    siex: 162, oficial: "BERENJENA", visible: "Berenjena", i18nKey: "eggplant", rango: 5,
    nombres: ["oberizini", "ngoyo", "茄子", "باذنجان", "berenjenas", "melanzana", "melanzane", "eggplant", "eggplants", "aubergine", "aubergines", "beringela", "berinjela"],
    erratas: ["berengena"],
    regfiweb: "Berenjena",
    etiquetas: { es: "Berenjena", en: "Eggplant", it: "Melanzana", fr: "Aubergine", pt: "Beringela", ar: "باذنجان", zh: "茄子", bm: "Oberizini", man: "Ngòyo" },
  },
  {
    siex: 163, oficial: "CALABACÍN", visible: "Calabacín", i18nKey: "zucchini", rango: 4,
    nombres: ["kurujeti", "kurujɛti", "西葫芦", "كوسة", "calabacin", "calabacines", "zucchina", "zucchine", "zucchini", "courgette", "courgettes", "abobrinha", "curgete"],
    regfiweb: "Calabacín",
    etiquetas: { es: "Calabacín", en: "Zucchini", it: "Zucchina", fr: "Courgette", pt: "Courgette", ar: "كوسة", zh: "西葫芦", bm: "Kurujɛti", man: "Kurujɛti" },
  },
  {
    siex: 153, oficial: "MELÓN", visible: "Melón", i18nKey: "melon", rango: 7,
    nombres: ["melɔn", "甜瓜", "شمام", "melon", "melones", "melone", "meloni", "melons", "melao", "meloa"],
    regfiweb: "Melón",
    etiquetas: { es: "Melón", en: "Melon", it: "Melone", fr: "Melon", pt: "Melão", ar: "شمام", zh: "甜瓜", bm: "Melɔn", man: "Melɔn" },
  },
  {
    siex: 156, oficial: "SANDÍA", visible: "Sandía", i18nKey: "watermelon", rango: 6,
    nombres: ["pasteki", "pastɛki", "西瓜", "بطيخ", "sandia", "sandias", "anguria", "angurie", "cocomero", "watermelon", "watermelons", "pasteque", "pasteques", "melancia"],
    regfiweb: "Sandía",
    etiquetas: { es: "Sandía", en: "Watermelon", it: "Anguria", fr: "Pastèque", pt: "Melancia", ar: "بطيخ", zh: "西瓜", bm: "Pastɛki", man: "Pastɛki" },
  },
  {
    // 180 JUDÍA, de baja en SIEX desde el 31/12/2022 → 49 ALUBIA (ver cabecera).
    siex: 49, oficial: "ALUBIA", visible: "Judía", i18nKey: "bean", rango: 8,
    nombres: ["shɔ", "soso", "菜豆", "فاصوليا", "judia", "judias", "judia verde", "judias verdes", "fagiolo", "fagioli", "fagiolino", "fagiolini", "bean", "beans", "green bean", "green beans", "haricot", "haricots", "haricot vert", "haricots verts", "feijao", "feijao verde", "alubia", "alubias"],
    regfiweb: "Judía verde",
    etiquetas: { es: "Judía", en: "Bean", it: "Fagiolo", fr: "Haricot", pt: "Feijão", ar: "فاصوليا", zh: "菜豆", bm: "Shɔ", man: "Sòso" },
  },
  {
    siex: 155, oficial: "LECHUGA", visible: "Lechuga", i18nKey: "lettuce", rango: 9,
    nombres: ["salati", "saladi", "生菜", "خس", "lechugas", "lattuga", "lattughe", "lettuce", "lettuces", "laitue", "laitues", "alface"],
    regfiweb: "Lechuga",
    etiquetas: { es: "Lechuga", en: "Lettuce", it: "Lattuga", fr: "Laitue", pt: "Alface", ar: "خس", zh: "生菜", bm: "Salati", man: "Saladi" },
  },
  {
    siex: 154, oficial: "BRÓCOLI", visible: "Brócoli", rango: 12,
    nombres: ["brocoli", "brocolis", "broccoli", "brocolo", "brecol", "brocolos", "brocolis"],
    regfiweb: "Bróculi",
    etiquetas: { es: "Brócoli", en: "Broccoli", it: "Broccoli", fr: "Brocoli", pt: "Brócolos" },
  },
  {
    siex: 160, oficial: "COLIFLOR", visible: "Coliflor", rango: 15,
    nombres: ["coliflores", "cavolfiore", "cavolfiori", "cauliflower", "chou-fleur", "chou fleur", "couve-flor", "couve flor"],
    regfiweb: "Coliflor",
    etiquetas: { es: "Coliflor", en: "Cauliflower", it: "Cavolfiore", fr: "Chou-fleur", pt: "Couve-flor" },
  },
  {
    // 170 COL, de baja desde el 31/12/2022 → 172 REPOLLO (ver cabecera).
    siex: 172, oficial: "REPOLLO", visible: "Col", rango: 14,
    nombres: ["repollo", "cavolo", "cabbage", "chou", "couve"],
    regfiweb: "Col",
    etiquetas: { es: "Col", en: "Cabbage", it: "Cavolo", fr: "Chou", pt: "Couve" },
  },
  {
    siex: 185, oficial: "CALABAZA", visible: "Calabaza", rango: 13,
    nombres: ["calabazas", "zucca", "zucche", "pumpkin", "courge", "potiron", "abobora"],
    // «squash» en inglés también es el calabacín (summer squash): solo sugerencia.
    tipos: ["squash"],
    etiquetas: { es: "Calabaza", en: "Pumpkin", it: "Zucca", fr: "Courge", pt: "Abóbora" },
  },
  {
    siex: 219, oficial: "FRESA", visible: "Fresa", rango: 10,
    nombres: ["fresas", "fragola", "fragole", "strawberry", "strawberries", "fraise", "fraises", "morango", "morangos"],
    // «fresón» es otro cultivo SIEX (938, abajo); el cuaderno lo sigue imprimiendo «Fresa».
    tipos: ["freson", "fresones"],
    regfiweb: "Fresal",
    etiquetas: { es: "Fresa", en: "Strawberry", it: "Fragola", fr: "Fraise", pt: "Morango" },
  },
  {
    siex: 279, oficial: "ESPÁRRAGO", visible: "Espárrago", rango: 22,
    nombres: ["esparrago", "esparragos", "asparago", "asparagi", "asparagus", "asperge", "asperges", "espargo"],
    etiquetas: { es: "Espárrago", en: "Asparagus", it: "Asparago", fr: "Asperge", pt: "Espargo" },
  },
  {
    siex: 183, oficial: "ESPINACA", visible: "Espinaca", rango: 17,
    nombres: ["espinacas", "spinacio", "spinaci", "spinach", "epinard", "epinards", "espinafre"],
    regfiweb: "Espinaca",
    etiquetas: { es: "Espinaca", en: "Spinach", it: "Spinacio", fr: "Épinard", pt: "Espinafre" },
  },
  {
    siex: 157, oficial: "CEBOLLA", visible: "Cebolla", rango: 23,
    nombres: ["cebollas", "cipolla", "cipolle", "onion", "onions", "oignon", "oignons", "cebola"],
    regfiweb: "Cebolla",
    etiquetas: { es: "Cebolla", en: "Onion", it: "Cipolla", fr: "Oignon", pt: "Cebola" },
  },
  {
    siex: 169, oficial: "AJO", visible: "Ajo", rango: 24,
    nombres: ["ajos", "aglio", "garlic", "ail", "alho"],
    regfiweb: "Ajo",
    etiquetas: { es: "Ajo", en: "Garlic", it: "Aglio", fr: "Ail", pt: "Alho" },
  },
  {
    siex: 99, oficial: "PATATA", visible: "Patata", rango: 25,
    nombres: ["patatas", "papa", "papas", "potato", "potatoes", "pomme de terre"],
    regfiweb: "Patata",
    etiquetas: { es: "Patata", en: "Potato", fr: "Pomme de terre" },
  },
  {
    siex: 178, oficial: "ZANAHORIA", visible: "Zanahoria", rango: 26,
    nombres: ["zanahorias", "carota", "carote", "carrot", "carrots", "carotte", "carottes", "cenoura"],
    regfiweb: "Zanahoria",
    etiquetas: { es: "Zanahoria", en: "Carrot", it: "Carota", fr: "Carotte", pt: "Cenoura" },
  },
  {
    siex: 158, oficial: "APIO", visible: "Apio", rango: 18,
    nombres: ["sedano", "celery", "celeri", "aipo"],
    regfiweb: "Apio",
    etiquetas: { es: "Apio", en: "Celery", it: "Sedano", fr: "Céleri", pt: "Aipo" },
  },
  {
    siex: 164, oficial: "ALCACHOFA", visible: "Alcachofa", rango: 21,
    nombres: ["alcachofas", "carciofo", "carciofi", "artichoke", "artichaut", "alcachofra"],
    etiquetas: { es: "Alcachofa", en: "Artichoke", it: "Carciofo", fr: "Artichaut", pt: "Alcachofra" },
  },
  {
    siex: 216, oficial: "ARÁNDANO", visible: "Arándano", rango: 27,
    nombres: ["arandano", "arandanos", "mirtillo", "mirtilli", "blueberry", "blueberries", "myrtille", "mirtilo"],
    etiquetas: { es: "Arándano", en: "Blueberry", it: "Mirtillo", fr: "Myrtille", pt: "Mirtilo" },
  },
  {
    siex: 192, oficial: "FRAMBUESA", visible: "Frambuesa", rango: 28,
    nombres: ["frambuesas", "lampone", "lamponi", "raspberry", "raspberries", "framboise", "framboesa"],
    etiquetas: { es: "Frambuesa", en: "Raspberry", it: "Lampone", fr: "Framboise", pt: "Framboesa" },
  },
  {
    siex: 343, oficial: "AGUACATE", visible: "Aguacate", rango: 29,
    nombres: ["aguacates", "avocado", "avocat", "abacate"],
    etiquetas: { es: "Aguacate", en: "Avocado", it: "Avocado", fr: "Avocat", pt: "Abacate" },
  },
  {
    siex: 344, oficial: "MANGO", visible: "Mango", rango: 30,
    nombres: ["mangos", "mangue"],
    etiquetas: { es: "Mango", fr: "Mangue" },
  },
  {
    siex: 214, oficial: "NÍSPERO", visible: "Níspero", rango: 31,
    nombres: ["nispero", "nisperos", "nespola", "loquat", "nefle", "nespera"],
    etiquetas: { es: "Níspero", en: "Loquat", it: "Nespola", fr: "Nèfle", pt: "Nêspera" },
  },

  // ── Fuera del cuaderno: los de CROP_ALIASES y el fresón (NO entran en su TABLA) ──
  {
    siex: 938, oficial: "FRESÓN", visible: "Fresón", rango: 11,
    nombres: ["fresones"],
    regfiweb: "Fresal",
    etiquetas: { es: "Fresón" },
  },
  {
    siex: 166, oficial: "ACELGA", visible: "Acelga", rango: 16,
    nombres: ["acelgas"],
    regfiweb: "Acelga",
    etiquetas: { es: "Acelga" },
  },
  {
    // 161 HABA FRESCA, de baja desde 2014 → 41 HABA.
    siex: 41, oficial: "HABA", visible: "Haba", rango: 19,
    nombres: ["habas", "haba verde", "habas verdes"],
    regfiweb: "Haba verde",
    etiquetas: { es: "Haba" },
  },
  {
    // 44 GUISANTE EN VERDE, de baja desde 2014 → 40 GUISANTE.
    siex: 40, oficial: "GUISANTE", visible: "Guisante", rango: 20,
    nombres: ["guisantes", "guisante verde", "guisantes verdes"],
    regfiweb: "Guisante verde",
    etiquetas: { es: "Guisante" },
  },
];

const POR_CODIGO: ReadonlyMap<number, CultivoCurado> = new Map(CULTIVOS_CURADOS.map((c) => [c.siex, c]));

/** El cultivo curado de un código SIEX, o undefined si no es de los curados. */
export function cultivoCuradoPorCodigo(code: number | null | undefined): CultivoCurado | undefined {
  return code == null ? undefined : POR_CODIGO.get(code);
}
